import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DbEntryDateContext } from './DbEntryDateContext';
import { toISODate, addDays } from '../lib/format';

// Global report filters: date range (preset or custom) + selected locations.
// Presets are anchored to the reporting snapshot date (dbEntryDate) rather
// than the wall clock, because the data always lags a few days behind.

export const PRESETS = [
  { id: 'yesterday', label: 'Latest day' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'ytd', label: 'Year to date' },
];

function presetRange(id, anchorISO) {
  const anchor = anchorISO; // latest business day with data
  const anchorDate = new Date(anchor + 'T12:00:00');
  switch (id) {
    case 'yesterday': return [anchor, anchor];
    case 'last7': return [addDays(anchor, -6), anchor];
    case 'last30': return [addDays(anchor, -29), anchor];
    case 'thisMonth': {
      const first = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
      return [toISODate(first), anchor];
    }
    case 'lastMonth': {
      const first = new Date(anchorDate.getFullYear(), anchorDate.getMonth() - 1, 1);
      const last = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 0);
      return [toISODate(first), toISODate(last)];
    }
    case 'ytd': return [`${anchorDate.getFullYear()}-01-01`, anchor];
    default: return [addDays(anchor, -6), anchor];
  }
}

const FilterContext = createContext(null);
export const useFilters = () => useContext(FilterContext);

export function FilterProvider({ children }) {
  const { dbEntryDate } = useContext(DbEntryDateContext);

  // Anchor = day before snapshot load (the last complete business day),
  // falling back to yesterday until the snapshot date arrives.
  const anchor = useMemo(() => {
    const base = dbEntryDate || toISODate(new Date());
    return addDays(base, -1);
  }, [dbEntryDate]);

  const [preset, setPreset] = useState('last7');
  const [customRange, setCustomRange] = useState(null); // [start, end] when preset === 'custom'
  // Comparison basis for delta chips: preceding window of equal length, or the
  // same window last year (364 days back so weekdays line up).
  const [compareMode, setCompareModeState] = useState(
    () => (localStorage.getItem('sd-compare-mode') === 'priorYear' ? 'priorYear' : 'prior')
  );
  const setCompareMode = (mode) => {
    setCompareModeState(mode);
    localStorage.setItem('sd-compare-mode', mode);
  };
  const compareLabel = compareMode === 'priorYear' ? 'vs same period last year' : 'vs prior period';
  const [locations, setLocations] = useState([]);        // [{ID, NAME, CITY, STATE}] — server-scoped per user
  const [selectedLocations, setSelectedLocations] = useState([]); // array of IDs; empty = all (of what the user may see)
  const [groups, setGroups] = useState({ city: [], province: [], custom: [] }); // named store groups
  const [me, setMe] = useState(null); // { email, role, locations } — null until loaded

  useEffect(() => {
    axios.get('/api/reports/locations')
      .then(res => setLocations(res.data || []))
      .catch(() => setLocations([]));
    axios.get('/api/reports/location-groups')
      .then(res => setGroups(res.data || { city: [], province: [], custom: [] }))
      .catch(() => {});
    axios.get('/api/me')
      .then(res => setMe(res.data))
      .catch(() => {});
  }, []);

  const [startDate, endDate] = useMemo(
    () => (preset === 'custom' && customRange ? customRange : presetRange(preset, anchor)),
    [preset, customRange, anchor]
  );

  const setCustom = (start, end) => {
    setCustomRange([start, end]);
    setPreset('custom');
  };

  // Params object shared by every report request
  const reportParams = useMemo(() => ({
    startDate,
    endDate,
    ...(selectedLocations.length > 0 ? { locations: selectedLocations.join(',') } : {}),
  }), [startDate, endDate, selectedLocations]);

  const value = {
    anchor,
    preset, setPreset,
    startDate, endDate, setCustom,
    locations,
    selectedLocations, setSelectedLocations,
    groups,
    me,
    reportParams,
    compareMode, setCompareMode, compareLabel,
    dbEntryDate,
  };

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}
